// Copyright 2026. WebPros International GmbH. All rights reserved.

import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'admin/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as computeResourceVmActions from 'common/modules/computeResourceVm/actions';
import * as computeResourceActions from 'admin/computeResource/actions';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import {
    Button,
    FormField,
    List,
    Text,
    Translate,
} from '@plesk/ui-library';
import { Loader } from 'common/components';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import ButtonWithConfirmation from 'common/components/ButtonWithConfirmation';
import { EmptyView } from 'common/components/EmptyView/EmptyView';
import { Dialog } from 'common/components/Dialog/Dialog';
import SelectInput from 'common/components/Select/SelectInput';
import { ISelectOption } from 'common/components/SelectInput';
import { IGpuResponse } from 'common/api/resources/Gpu';

export type VmGpuTabProps =
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

type GpuConnectionType = 'vgpu' | 'passthrough';

interface IConnectionTypeOption extends ISelectOption {
    value: GpuConnectionType;
    label: JSX.Element;
}

interface IGpuChoiceOption extends ISelectOption {
    value: number;
    label: string;
}

interface IGpuRow {
    id: string;
    colGpuName: string;
    colType: JSX.Element;
    colDetail: string;
    colAddress: string;
    colActions: JSX.Element;
}

const COLUMNS = [
    {
        key: 'colGpuName',
        title: <Translate content="computeResourceVm.vgpu.columns.gpuName" />,
    },
    {
        key: 'colType',
        title: <Translate content="computeResourceVm.vgpu.columns.type" />,
    },
    {
        key: 'colDetail',
        title: <Translate content="computeResourceVm.vgpu.columns.detail" />,
    },
    {
        key: 'colAddress',
        title: <Translate content="computeResourceVm.vgpu.columns.address" />,
    },
    {
        key: 'colActions',
        title: '',
        width: '1%',
    },
];

const CONNECTION_TYPE_OPTIONS: IConnectionTypeOption[] = [
    { value: 'vgpu', label: <Translate content="computeResourceVm.vgpu.type.vgpu" /> },
    { value: 'passthrough', label: <Translate content="computeResourceVm.vgpu.type.passthrough" /> },
];

export const profileOptionsFromGpus = (gpus: IGpuResponse[]): IGpuChoiceOption[] => {
    const byProfileId = new Map<number, IGpuChoiceOption>();

    gpus.forEach((gpu) => {
        if (!gpu.current_profile || !gpu.placement_addresses.some(placement => !placement.server)) {
            return;
        }

        const profile = gpu.available_profiles.find(candidate => candidate.name === gpu.current_profile);
        if (!profile || byProfileId.has(profile.id)) {
            return;
        }

        byProfileId.set(profile.id, { value: profile.id, label: profile.name });
    });

    return Array.from(byProfileId.values());
};

export const passthroughCardOptionsFromGpus = (gpus: IGpuResponse[]): IGpuChoiceOption[] => gpus
    .filter(gpu => gpu.allocation_mode === 'passthrough'
        && gpu.status === 'active'
        && !gpu.is_processing
        && gpu.passthrough_attachment?.server === null)
    .map(gpu => ({
        value: gpu.id,
        label: gpu.name,
    }));

const VmGpuTab: React.FC<VmGpuTabProps> = ({
    serverId,
    computeResourceId,
    vgpuPlacements,
    gpuPassthroughAttachments,
    gpus,
    isLoading,
    isLoadingGpus,
    isAttaching,
    isDetaching,
    isServerProcessing,
    getServerVgpuPlacements,
    getServerGpuPassthroughAttachments,
    getGpus,
    attachVgpu,
    attachGpuPassthrough,
    detachVgpu,
    detachGpuPassthrough,
}) => {
    const [isDialogOpen, setDialogOpen] = React.useState(false);
    const [connectionType, setConnectionType] = React.useState<GpuConnectionType | null>(null);
    const [selectedChoice, setSelectedChoice] = React.useState<IGpuChoiceOption | null>(null);

    React.useEffect(() => {
        if (serverId > 0) {
            getServerVgpuPlacements(serverId);
            getServerGpuPassthroughAttachments(serverId);
        }
    }, [serverId, getServerVgpuPlacements, getServerGpuPassthroughAttachments]);

    const openDialog = React.useCallback(() => {
        setDialogOpen(true);
        if (computeResourceId > 0) {
            getGpus(computeResourceId);
        }
    }, [computeResourceId, getGpus]);

    const closeDialog = React.useCallback(() => {
        setDialogOpen(false);
        setConnectionType(null);
        setSelectedChoice(null);
    }, []);

    const handleTypeChange = React.useCallback((option: IConnectionTypeOption | null) => {
        setConnectionType(option?.value ?? null);
        setSelectedChoice(null);
    }, []);

    const profileOptions = React.useMemo(() => profileOptionsFromGpus(gpus), [gpus]);
    const cardOptions = React.useMemo(() => passthroughCardOptionsFromGpus(gpus), [gpus]);

    const handleAttach = React.useCallback(async () => {
        if (!connectionType || !selectedChoice) {
            return;
        }

        try {
            if (connectionType === 'vgpu') {
                await attachVgpu(serverId, { gpu_profile_id: selectedChoice.value });
            } else {
                await attachGpuPassthrough(serverId, selectedChoice.value);
            }
            closeDialog();
        } catch {
            // Errors handled by global error handler.
        }
    }, [serverId, connectionType, selectedChoice, attachVgpu, attachGpuPassthrough, closeDialog]);

    const renderDetach = (onConfirm: () => void) => (
        <ButtonWithConfirmation
            isLoading={isDetaching || isServerProcessing}
            disabled={isServerProcessing}
            handleConfirm={onConfirm}
            confirmationButtonText={<Translate content="computeResourceVm.vgpu.detachBtn" />}
            confirmationButtonGhost={false}
            buttonIntent={INTENT_TYPE.DANGER}
            buttonSize={SIZE.MD}
            translations={{
                title: <Translate content="computeResourceVm.vgpu.detachBtn" />,
                text: <Translate content="computeResourceVm.vgpu.detachConfirmation" />,
                button: <Translate content="computeResourceVm.vgpu.detachBtn" />,
                tooltip: isServerProcessing
                    ? <Translate content="computeResourceVm.vgpu.processingTooltip" />
                    : undefined,
            }}
        />
    );

    const rows: IGpuRow[] = [
        ...vgpuPlacements.map(placement => ({
            id: `vgpu-${placement.id}`,
            colGpuName: placement.gpu?.name ?? '-',
            colType: <Translate content="computeResourceVm.vgpu.type.vgpu" />,
            colDetail: placement.gpu?.current_profile ?? '-',
            colAddress: placement.address,
            colActions: renderDetach(() => detachVgpu(serverId, placement.id)),
        })),
        ...gpuPassthroughAttachments.map(attachment => ({
            id: `passthrough-${attachment.id}`,
            colGpuName: attachment.gpu?.name ?? '-',
            colType: <Translate content="computeResourceVm.vgpu.type.passthrough" />,
            colDetail: attachment.gpu?.gpu_model?.name ?? '-',
            colAddress: attachment.gpu?.pci_address ?? '-',
            colActions: renderDetach(() => detachGpuPassthrough(serverId, attachment.id)),
        })),
    ];

    const choiceOptions = connectionType === 'passthrough' ? cardOptions : profileOptions;
    const choiceLabelKey = connectionType === 'passthrough'
        ? 'computeResourceVm.vgpu.dialog.selectCard'
        : 'computeResourceVm.vgpu.dialog.selectProfile';

    return (
        <Loader isLoading={isLoading}>
            {rows.length > 0 ? (
                <List
                    columns={COLUMNS}
                    data={rows}
                    rowKey="id"
                />
            ) : (
                <EmptyView
                    title="computeResourceVm.vgpu.emptyView.title"
                    description="computeResourceVm.vgpu.emptyView.description"
                    buttonText="computeResourceVm.vgpu.emptyView.buttonText"
                    onButtonClick={openDialog}
                    hideIcon={true}
                />
            )}

            <Dialog
                isOpen={isDialogOpen}
                closeHandler={closeDialog}
                size={SIZE.SM}
                heading={<Translate content="computeResourceVm.vgpu.dialog.title" />}
            >
                <FormField
                    name="allocation_mode"
                    label={<Translate content="computeResourceVm.vgpu.dialog.selectType" />}
                    vertical
                >
                    {({ getId }) => (
                        <SelectInput<IConnectionTypeOption>
                            inputId={getId()}
                            menuPosition="fixed"
                            options={CONNECTION_TYPE_OPTIONS}
                            isClearable={false}
                            isSearchable={false}
                            value={CONNECTION_TYPE_OPTIONS.find(option => option.value === connectionType) ?? null}
                            onChange={(option) => handleTypeChange(option as IConnectionTypeOption | null)}
                            placeholder={<Translate content="computeResourceVm.vgpu.dialog.selectTypePlaceholder" />}
                        />
                    )}
                </FormField>

                {connectionType !== null && (
                    <FormField
                        name="gpu_choice"
                        label={<Translate content={choiceLabelKey} />}
                        vertical
                    >
                        {({ getId }) => (
                            <SelectInput<IGpuChoiceOption>
                                inputId={getId()}
                                menuPosition="fixed"
                                options={choiceOptions}
                                isLoading={isLoadingGpus}
                                isClearable={false}
                                value={selectedChoice}
                                onChange={(option) => setSelectedChoice(option as IGpuChoiceOption)}
                                placeholder={<Translate content="computeResourceVm.vgpu.dialog.selectPlaceholder" />}
                            />
                        )}
                    </FormField>
                )}

                <Text intent={INTENT_TYPE.MUTED} fontSize={SIZE.SM}>
                    <Translate content="computeResourceVm.vgpu.dialog.restartNote" />
                </Text>

                <Button
                    intent={INTENT_TYPE.PRIMARY}
                    size={SIZE.LG}
                    fill={true}
                    disabled={!connectionType || !selectedChoice}
                    state={isAttaching ? 'loading' : undefined}
                    onClick={handleAttach}
                >
                    <Translate content="computeResourceVm.vgpu.dialog.attachBtn" />
                </Button>
            </Dialog>
        </Loader>
    );
};

const mapStateToProps = (state: RootState) => ({
    serverId: state.computeResourceVm.item.id,
    computeResourceId: state.computeResourceVm.item.compute_resource?.id ?? 0,
    vgpuPlacements: state.computeResourceVm.vgpuPlacements,
    gpuPassthroughAttachments: state.computeResourceVm.gpuPassthroughAttachments,
    gpus: state.computeResource.gpus,
    isLoading: state.app.loadingFlags.has(LOADING_FLAGS.SERVER_VGPU_PLACEMENTS)
        || state.app.loadingFlags.has(LOADING_FLAGS.SERVER_GPU_PASSTHROUGH_ATTACHMENTS),
    isLoadingGpus: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_GPUS),
    isAttaching: state.app.loadingFlags.has(LOADING_FLAGS.ATTACH_VGPU)
        || state.app.loadingFlags.has(LOADING_FLAGS.ATTACH_GPU_PASSTHROUGH),
    isDetaching: state.app.loadingFlags.has(LOADING_FLAGS.DETACH_VGPU)
        || state.app.loadingFlags.has(LOADING_FLAGS.DETACH_GPU_PASSTHROUGH),
    isServerProcessing: state.computeResourceVm.item.is_processing,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getServerVgpuPlacements: bindActionCreators(computeResourceVmActions.getServerVgpuPlacements, dispatch),
    getServerGpuPassthroughAttachments: bindActionCreators(computeResourceVmActions.getServerGpuPassthroughAttachments, dispatch),
    getGpus: bindActionCreators(computeResourceActions.getGpus, dispatch),
    attachVgpu: bindActionCreators(computeResourceVmActions.attachVgpu, dispatch),
    attachGpuPassthrough: bindActionCreators(computeResourceVmActions.attachGpuPassthrough, dispatch),
    detachVgpu: bindActionCreators(computeResourceVmActions.detachVgpu, dispatch),
    detachGpuPassthrough: bindActionCreators(computeResourceVmActions.detachGpuPassthrough, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(VmGpuTab);
